import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ChevronRight, Zap, Radio, FlaskConical, Cpu, Activity } from 'lucide-react';
import SEO from '../components/SEO';

const topics = [
  {
    category: 'Fundamentals',
    color: 'green',
    icon: Zap,
    lessons: [
      {
        title: "Ohm's Law",
        desc: "Voltage = Current × Resistance. The foundation of all circuit analysis.",
        detail: "V = I × R. If you know any two values, you can always calculate the third. Essential for sizing resistors, calculating current draw, and understanding voltage drops.",
      },
      {
        title: "Kirchhoff's Laws",
        desc: "KVL: voltages around a loop sum to zero. KCL: currents into a node sum to zero.",
        detail: "KVL (Voltage Law): The sum of voltages around any closed loop is 0. KCL (Current Law): The sum of currents entering a node equals the sum leaving it.",
      },
      {
        title: "Series vs Parallel Circuits",
        desc: "Series: same current, voltages add. Parallel: same voltage, currents add.",
        detail: "Series: R_total = R1 + R2 + ... — current is the same throughout. Parallel: 1/R_total = 1/R1 + 1/R2 + ... — voltage is the same across each branch.",
      },
      {
        title: "Power Dissipation",
        desc: "P = V × I = I²R = V²/R. Know how hot your components will get.",
        detail: "Always check power ratings on resistors. A 1/4W resistor can handle 0.25W. Use P = I²R to check if a component will overheat before you smoke it.",
      },
    ],
  },
  {
    category: 'Components',
    color: 'cyan',
    icon: Cpu,
    lessons: [
      {
        title: "Resistors",
        desc: "Limit current flow. Read color bands or use a multimeter for value.",
        detail: "4-band code: Band 1 & 2 = digits, Band 3 = multiplier, Band 4 = tolerance. Gold = ±5%, Silver = ±10%. Always use resistors to protect LEDs from burning out.",
      },
      {
        title: "Capacitors",
        desc: "Store charge, filter noise, smooth power rails, and decouple circuits.",
        detail: "Electrolytic caps are polarized (mind the + side!). Ceramic caps are non-polarized. Use 100nF ceramics near every IC power pin for decoupling. Caps block DC but pass AC.",
      },
      {
        title: "Transistors (NPN/PNP)",
        desc: "Electronic switches and amplifiers. BJT and MOSFET are the two main families.",
        detail: "NPN BJT: Base high → Collector-Emitter conducts. Great for switching loads. N-channel MOSFET: Gate high → Drain-Source conducts. Preferred for power switching — less gate current needed.",
      },
      {
        title: "Diodes & LEDs",
        desc: "Allow current in one direction only. LEDs emit light when forward biased.",
        detail: "Standard forward voltage: Si diode ~0.7V, LED ~2V (red) to ~3.5V (blue/white). Always use a current-limiting resistor with LEDs: R = (Vsupply - Vf) / If.",
      },
    ],
  },
  {
    category: 'Practical Skills',
    color: 'amber',
    icon: FlaskConical,
    lessons: [
      {
        title: "Soldering Tips",
        desc: "Heat the joint, not the solder. Clean tip = good solder flow.",
        detail: "Set iron to 320-370°C for lead-free. Tin the tip first. Place iron on the joint for 2s, then apply solder to the joint (not the iron). Good joint = shiny, volcano-shaped. Bad = dull/grainy (cold joint).",
      },
      {
        title: "Using a Multimeter",
        desc: "Measure voltage (V), current (A), resistance (Ω), and continuity.",
        detail: "Continuity mode: beeps if connected — use to check traces, find shorts. Always set to a range ABOVE what you expect to measure. Never measure resistance on a live circuit.",
      },
      {
        title: "Reading a Datasheet",
        desc: "The spec sheet is your bible. Absolute maximum ratings = do not exceed these.",
        detail: "Key sections: Absolute Maximum Ratings (never exceed), Electrical Characteristics (typical operating values), Pin Description (what each pin does), Application Circuit (reference design to copy).",
      },
      {
        title: "ESD Protection",
        desc: "Static electricity kills ICs. Ground yourself before handling sensitive components.",
        detail: "Use an anti-static wrist strap connected to earth ground when handling ICs, MOSFETs, and microcontrollers. Store ICs in anti-static bags. Touch a grounded metal surface before picking up components.",
      },
    ],
  },
  {
    category: 'Microcontrollers',
    color: 'purple',
    icon: Radio,
    lessons: [
      {
        title: "Arduino Basics",
        desc: "setup() runs once. loop() runs forever. Digital I/O uses pins 0-13.",
        detail: "pinMode(pin, OUTPUT/INPUT). digitalWrite(pin, HIGH/LOW). digitalRead(pin). analogRead(A0-A5) returns 0-1023. analogWrite(pin, 0-255) for PWM. Always initialize Serial.begin(9600) for debugging.",
      },
      {
        title: "I2C vs SPI vs UART",
        desc: "Three main serial protocols for talking between chips and modules.",
        detail: "UART: 2 wires (TX/RX), async, simple — for GPS, Bluetooth. SPI: 4 wires, fast, master-slave — for displays, SD cards. I2C: 2 wires (SDA/SCL), multi-device, slower — for sensors, EEPROMs. Each device gets a 7-bit address.",
      },
      {
        title: "PWM (Pulse Width Modulation)",
        desc: "Fake analog output using rapid on/off switching. Controls motor speed, LED brightness.",
        detail: "PWM uses a duty cycle: 50% = half power. Arduino analogWrite(pin, 128) = 50% duty cycle at ~490Hz. Use PWM pins (marked with ~) on Arduino. Motor controllers use PWM for speed control.",
      },
      {
        title: "GPIO & Pull-up Resistors",
        desc: "Float inputs are noisy. Use pull-up or pull-down resistors to define a default state.",
        detail: "An input pin with nothing connected reads random noise. Pull-up: connect pin to VCC via 10kΩ — default HIGH, button pull LOW. Arduino has built-in pull-ups: pinMode(pin, INPUT_PULLUP).",
      },
    ],
  },
  {
    category: 'Jigs & Fixtures',
    color: 'green',
    icon: Activity,
    lessons: [
      {
        title: "Testing Jigs",
        desc: "Pogo-pin fixtures for rapid PCB testing without soldering.",
        detail: "Use pogo pins and a laser-cut base to create a bed-of-nails tester. This allows you to test power rails and signal continuity on dozens of boards in seconds.",
      },
      {
        title: "Logic Probes",
        desc: "Quickly check high/low states and pulse activity on a line.",
        detail: "A logic probe is faster than a scope for simple 'is it blinking?' checks. High LED = 1, Low LED = 0, Pulsing LED = data activity. Essential for digital bus troubleshooting.",
      },
      {
        title: "Breakout Boards",
        desc: "Convert tiny SMD packages into breadboard-friendly headers.",
        detail: "Don't struggle with 0.5mm pitch ICs. Use breakout boards (BOBs) for prototyping. They make it easy to probe signals and swap components during development.",
      },
    ],
  },
];

const colorMap: Record<string, { text: string; bg: string; border: string }> = {
  green: { text: 'var(--color-primary)', bg: 'rgba(0,255,136,0.08)', border: 'rgba(0,255,136,0.2)' },
  cyan: { text: 'var(--color-secondary)', bg: 'rgba(0,207,255,0.08)', border: 'rgba(0,207,255,0.2)' },
  amber: { text: 'var(--color-accent)', bg: 'rgba(255,170,0,0.08)', border: 'rgba(255,170,0,0.2)' },
  purple: { text: 'var(--color-purple)', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.2)' },
};

export default function Learn() {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem', paddingBottom: '6rem' }}>
      <SEO 
        title="Electronics Learning & Tutorials | DIYElectronicMech" 
        description="Master electronics with interactive tutorials, guides, and practical maker projects. Learn Ohm's law, component basics, and circuit design."
      />
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <div style={{ padding: '2.5rem 0 2rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{ width: '52px', height: '52px', background: 'rgba(0,207,255,0.1)', border: '1px solid rgba(0,207,255,0.3)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GraduationCap size={24} color="var(--color-secondary)" />
            </div>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Learn <span style={{ color: 'var(--color-secondary)' }}>Electronics</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto', fontSize: '0.9rem' }}>
            Master the fundamentals. Practical knowledge for makers, hobbyists, and repair technicians.
          </p>
        </div>

        {topics.map(({ category, color, icon: Icon, lessons }) => {
          const c = colorMap[color];
          return (
            <div key={category} style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
                <div style={{ width: '32px', height: '32px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} color={c.text} />
                </div>
                <h2 style={{ fontSize: '1rem', fontWeight: 700, color: c.text, letterSpacing: '-0.01em' }}>{category}</h2>
                <div style={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${c.border}, transparent)` }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
                {lessons.map(({ title, desc, detail }) => (
                  <div key={title} 
                    onClick={() => setExpandedCard(expandedCard === title ? null : title)}
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '1.25rem', transition: 'all 0.2s ease', cursor: 'pointer' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = c.border; (e.currentTarget as HTMLElement).style.background = `linear-gradient(135deg, ${c.bg}, var(--bg-surface))`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: c.text, marginBottom: '0.35rem' }}>{title}</h3>
                      <ChevronRight size={16} color={c.text} style={{ transform: expandedCard === title ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                    </div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem' }}>{desc}</p>
                    
                    {expandedCard === title && (
                      <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
                        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.6, fontFamily: 'var(--font-body)' }}>{detail}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Ask AI CTA */}
        <div style={{ background: 'rgba(0,207,255,0.06)', border: '1px solid rgba(0,207,255,0.2)', borderRadius: '14px', padding: '2rem', textAlign: 'center' }}>
          <Activity size={22} color="var(--color-secondary)" style={{ marginBottom: '0.75rem' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.4rem' }}>Still confused?</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '1rem' }}>
            Ask ElectrAI any electronics question and get a clear, practical explanation instantly.
          </p>
          <Link to="/diagnose" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(0,207,255,0.12)', color: 'var(--color-secondary)', border: '1px solid rgba(0,207,255,0.3)', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.83rem', fontWeight: 600, textDecoration: 'none' }}>
            Ask ElectrAI <ChevronRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  );
}
